"use client";

import Image from "next/image";
import { ArrowRight } from "lucide-react";

/**
 * Reusable App Hero Section
 * Full-bleed hero with background image, centered text content, and CTA
 * Responsive: desktop (full bg), tablet (narrower), mobile (compact)
 */
export const AppHeroSection = ({
  brand = "Keystone",
  title,
  description,
  ctaText = "Install now",
  ctaLink = "#",
  secondaryCtaText,
  secondaryCtaLink,
  iconSrc,
  heroImage,
  backgroundSvg = "/background.svg",
}) => {
  return (
    <section className="sticky top-0 z-0 overflow-hidden bg-[#1e1e1e] mx-auto">
      {/* SVG Background. Deliberately not `priority`: it is decorative and was
          competing for bandwidth with the hero card below, which is the actual
          LCP element. `sizes` stops `fill` from defaulting to 100vw and pulling
          the 3840px candidate out of deviceSizes. */}
      <div className="absolute inset-0 pointer-events-none">
        <Image
          src={backgroundSvg}
          alt=""
          fill
          sizes="100vw"
          className="object-cover"
        />
      </div>

      {/* Desktop hero (md+) */}
      <div className="hidden md:flex flex-col items-center pt-[220px] px-20 relative z-10">
        <div className="flex flex-col gap-8 items-center w-full max-w-[1281px]">
          {/* App Icon */}
          {iconSrc && (
            <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0">
              <Image
                src={iconSrc}
                alt=""
                width={64}
                height={64}
                className="object-contain"
              />
            </div>
          )}

          {/* Introduction */}
          <div className="flex flex-col gap-4 items-center w-full">
            <h1
              className="text-[48px] font-normal text-[#f8f7f4] text-center tracking-[-0.96px] w-full"
              style={{ lineHeight: "56px" }}
            >
              {brand}
              {brand ? " " : ""}
              {title}
            </h1>
            <p
              className="text-xl font-normal text-[#c4c4c4] text-center tracking-[-0.04px] w-full"
              style={{ lineHeight: "28px" }}
            >
              {description}
            </p>
          </div>

          {/* CTA */}
          <div className="flex flex-wrap items-stretch justify-center gap-3 w-full">
            <a
              href={ctaLink}
              className="group inline-flex items-center h-[52px] bg-[#dbdacd] border border-[#f8f7f4] rounded-[10px] overflow-hidden px-4 shadow-[inset_0px_0px_0px_0px_transparent] hover:bg-[#1e1e1e] hover:border-[#606060] hover:shadow-[inset_0px_0px_8.7px_1px_#bfbeb8] transition-[background-color,border-color,box-shadow] duration-500 ease-in-out"
            >
              <span
                className="text-base font-medium text-[#3e3e3e] group-hover:text-[#dbdacd] tracking-[-0.144px] px-2 transition-colors duration-500"
                style={{ lineHeight: "20px" }}
              >
                {ctaText}
              </span>
              <ArrowRight className="w-5 h-5 text-[#3e3e3e] group-hover:text-[#dbdacd] transition-colors duration-500" />
            </a>
            {secondaryCtaText && secondaryCtaLink && (
              <a
                href={secondaryCtaLink}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center h-[52px] bg-white/5 border border-white/30 rounded-[10px] overflow-hidden px-4 hover:bg-white/10 hover:border-white/60 transition-[background-color,border-color] duration-500 ease-in-out"
              >
                <span
                  className="text-base font-medium text-[#dbdacd] tracking-[-0.144px] px-2"
                  style={{ lineHeight: "20px" }}
                >
                  {secondaryCtaText}
                </span>
                <ArrowRight className="w-5 h-5 text-[#dbdacd]" />
              </a>
            )}
          </div>

          {/* Hero Image — the LCP element on this page. `sizes` caps the
              srcset at the container's real 1281px instead of letting the
              1281px declared width imply a 2562px 2x candidate. */}
          {heroImage && (
            <div className="w-full flex justify-center">
              <Image
                src={heroImage}
                alt={`${brand ? `${brand} ` : ""}${title} app dashboard preview`}
                width={1281}
                height={600}
                sizes="(max-width: 1281px) 100vw, 1281px"
                className="w-full h-auto"
                priority
              />
            </div>
          )}
        </div>
      </div>

      {/* Mobile hero (below md) */}
      <div className="flex md:hidden flex-col items-center pt-[220px] px-4 pb-8 relative z-10">
        <div className="flex flex-col gap-10 items-center w-full max-w-[288px]">
          {/* App Icon */}
          {iconSrc && (
            <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0">
              <Image
                src={iconSrc}
                alt=""
                width={64}
                height={64}
                className="object-contain"
              />
            </div>
          )}

          {/* Introduction */}
          {/* role=heading keeps mobile a11y while the desktop <h1> stays the
              single h1 in the DOM (crawlers see both blocks) */}
          <div className="flex flex-col gap-6 items-center w-full">
            <p
              role="heading"
              aria-level="1"
              className="text-[32px] font-normal text-[#f8f7f4] text-center tracking-[-0.64px] w-full"
              style={{ lineHeight: "40px" }}
            >
              {brand}
              {brand && <br />}
              {title}
            </p>
            <p
              className="text-sm font-normal text-[#f8f7f4]/80 text-center tracking-[-0.028px] max-w-[245px]"
              style={{ lineHeight: "20px" }}
            >
              {description}
            </p>
          </div>

          {/* CTA */}
          <div className="flex flex-col items-center gap-3">
            <a
              href={ctaLink}
              className="inline-flex items-center justify-center h-[52px] bg-[#dbdacd] border border-[#f8f7f4] rounded-[10px] overflow-hidden px-6 hover:bg-[#cccbbe] transition-colors"
            >
              <span
                className="text-base font-medium text-[#3e3e3e] tracking-[-0.144px] px-2"
                style={{ lineHeight: "20px" }}
              >
                {ctaText}
              </span>
              <ArrowRight className="w-5 h-5 text-[#3e3e3e]" />
            </a>
            {secondaryCtaText && secondaryCtaLink && (
              <a
                href={secondaryCtaLink}
                target="_blank"
                  rel="noopener noreferrer"
                className="inline-flex items-center justify-center h-[52px] bg-white/5 border border-white/30 rounded-[10px] overflow-hidden px-6 hover:bg-white/10 hover:border-white/60 transition-[background-color,border-color] duration-500 ease-in-out"
              >
                <span
                  className="text-base font-medium text-[#dbdacd] tracking-[-0.144px] px-2"
                  style={{ lineHeight: "20px" }}
                >
                  {secondaryCtaText}
                </span>
                <ArrowRight className="w-5 h-5 text-[#dbdacd]" />
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
